import { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import '../css/NumberComboField.css';

/**
 * A number field that is also a dropdown: type any value, or open the
 * chevron and pick a suggested one.
 *
 * Built rather than using <input list> + <datalist>, which is the native
 * version of exactly this. Chrome draws NO affordance for a datalist — no
 * chevron, nothing — so the dropdown half would be invisible unless the user
 * already knew it was there, and Safari ignores datalist altogether.
 *
 * @param {string|number} value - current value (controlled)
 * @param {(next: string) => void} onChange - receives the raw string, so the
 *   caller keeps control of parsing and validation
 * @param {number[]} options - suggested values shown in the dropdown
 * @param {string} [id] - id for the input, for an external <label htmlFor>
 * @param {string} [ariaLabel] - accessible name for the input
 * @param {string} [optionsLabel] - accessible name for the dropdown list
 * @param {number} [min] - passed through to the input
 */
function NumberComboField({
  value,
  onChange,
  options = [],
  id,
  ariaLabel = 'Value',
  optionsLabel = 'Suggested values',
  min = 1,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on a click anywhere else, or on Escape. Bound only while open, so
  // a screen holding several of these isn't carrying idle document
  // listeners for each one.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      // Stopped from bubbling: this is typically rendered inside a modal,
      // and Escape would otherwise close the whole form out from under
      // someone who only meant to dismiss the list.
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  return (
    <div className="ncf-combo" ref={wrapRef}>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        step="1"
        className="ncf-combo-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        className="ncf-combo-toggle"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? `Hide ${optionsLabel.toLowerCase()}` : `Show ${optionsLabel.toLowerCase()}`}
        aria-expanded={open}
        // Skipped in the tab order: the field beside it is the real control,
        // and tabbing into a button that only opens a shortcut list is a
        // stop that costs more than it gives.
        tabIndex={-1}
      >
        <ChevronDown size={16} />
      </button>

      {open && (
        <ul className="ncf-combo-list" role="listbox" aria-label={optionsLabel}>
          {options.map(n => (
            <li key={n} role="option" aria-selected={Number(value) === n}>
              <button
                type="button"
                className={`ncf-combo-opt${Number(value) === n ? ' active' : ''}`}
                onClick={() => { onChange(String(n)); setOpen(false); }}
              >
                {n}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default NumberComboField;
